import type { LinkProps } from 'next/link';

import {
  getCrossChainEstimateSeconds,
  isBridgeCrossChainStatus,
} from '@/lib/api/0x-cross-chain/status';

import { CHAIN_IDS, getSettlementTime } from '@/constants/chain';

import type { Transaction } from '@/store/transaction/types';

import { getBlockExplorerTransactionURL } from '@/utils/blockExplorers';
import { routes } from '@/utils/routes';

type DetailsLink = { label: string } & (
  | { external: true; href: string }
  | { external: false; href: LinkProps['href'] }
);

type PendingStatus = {
  title: string;
  body: string;
  estimatedSeconds: number;
  details?: DetailsLink;
};

/**
 * Copy, settlement estimate, and details link for the pending screen. Same-chain
 * trades use measured settlement times; cross-chain trades use the quote's estimate.
 */
export const getPendingStatus = (
  transaction: Transaction,
  elapsedSeconds: number,
): PendingStatus => {
  const { sellToken, hash } = transaction;

  if (transaction.transactionType === 'cross-chain') {
    const hasSwapStep = transaction.quoteSteps?.some((step) => step.type.toLowerCase() === 'swap');
    const isBridgeOnly = (transaction.quoteSteps?.length ?? 0) > 0 && !hasSwapStep;
    const isBridging = isBridgeOnly || isBridgeCrossChainStatus(transaction.tradeStatus?.status);
    return {
      title: isBridging ? 'Bridging...' : 'Confirming transaction...',
      body: isBridging
        ? 'Funds are being bridged to the destination chain.'
        : 'Transaction is being confirmed on the origin chain.',
      estimatedSeconds: getCrossChainEstimateSeconds(sellToken.chainId, transaction.serviceTime),
      details: {
        label: 'See details',
        href: { pathname: routes.TX, query: { chainId: String(sellToken.chainId), txHash: hash } },
        external: false,
      },
    };
  }

  const { typical, slow } = getSettlementTime(
    sellToken.chainId,
    transaction.transactionType === 'intents' ? 'auto' : 'standard',
  );
  // Same-chain intents and wraps have no explorer page worth linking; `hash` is
  // the trade hash for intents.
  const hasExplorerPage =
    transaction.transactionType === 'swap' || transaction.transactionType === 'solana-swap';
  return {
    title: 'Confirming transaction...',
    body:
      elapsedSeconds > slow
        ? 'This is taking longer than usual.'
        : 'Your trade is being confirmed.',
    estimatedSeconds: typical,
    details: hasExplorerPage
      ? {
          label: sellToken.chainId === CHAIN_IDS.SOLANA ? 'See on Solscan' : 'See details',
          href: getBlockExplorerTransactionURL(sellToken.chainId, hash),
          external: true,
        }
      : undefined,
  };
};
